import { Component, EventEmitter, ViewChild, Input, Output, ElementRef, Renderer2, forwardRef, SimpleChanges, OnChanges } from '@angular/core';
import { NG_VALUE_ACCESSOR, NG_VALIDATORS, AbstractControl, ValidationErrors, ControlValueAccessor, Validator, Validators } from '@angular/forms';
import { Utils } from '../utils';
import { Helper, createMinValidator, createMaxValidator } from './numeric-textbox.helper';

const keyCodes: Record<string, number> = {
  enter: 13,
  escape: 27,
  left: 37,
  up: 38,
  right: 39,
  down: 40,
};

@Component({
  selector: 'app-numeric-textbox',
  templateUrl: './numeric-textbox.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => NumericTextboxComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => NumericTextboxComponent),
      multi: true,
    },
  ],
  exportAs: 'numericTextbox',
  standalone: false,
})
export class NumericTextboxComponent implements ControlValueAccessor, Validator, OnChanges {
  @ViewChild('numericInput', { static: true }) numericInput: ElementRef;
  @Input() min = Number.MIN_SAFE_INTEGER;
  @Input() max = Number.MAX_SAFE_INTEGER;
  @Input() value: number;
  @Input() placeholder: string;
  @Input() decimals = 2;
  @Input() disabled = false;
  @Input() format = '0,0.00';
  @Input() autoCorrect = false;
  @Input() rangeValidation = true;
  @Output() valueChange = new EventEmitter<number>();
  @Output() focus = new EventEmitter();
  @Output() blur = new EventEmitter();
  @Output() enter = new EventEmitter();
  @Output() escape = new EventEmitter();
  private minValidateFn = Validators.nullValidator;
  private maxValidateFn = Validators.nullValidator;
  private focused = false;
  private inputValue: string;
  private previousValue = undefined;
  private numericRegex: RegExp;
  private onChange = (value: number) => {};
  private onTouched = () => {};

  constructor(private readonly renderer: Renderer2) {}

  public focusInput() {
    this.numericInput.nativeElement.focus();
  }

  public blurInput() {
    this.numericInput.nativeElement.blur();
  }

  public validate(control: AbstractControl): ValidationErrors | null {
    return this.minValidateFn(control) || this.maxValidateFn(control);
  }

  public writeValue(value: number): void {
    const newValue = this.restrictModelValue(value);
    this.value = newValue;
    this.setInputValue();
  }

  public registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  public ngOnChanges(changes: SimpleChanges): void {
    this.verifySettings();

    if (Helper.anyChanges(['autoCorrect', 'decimals'], changes)) {
      delete this.numericRegex;
    }

    if (Helper.anyChanges(['min', 'max', 'rangeValidation'], changes)) {
      if (Utils.isNumber(this.min) && this.rangeValidation) {
        this.minValidateFn = createMinValidator(this.min);
      } else {
        this.minValidateFn = Validators.nullValidator;
      }

      if (Utils.isNumber(this.max) && this.rangeValidation) {
        this.maxValidateFn = createMaxValidator(this.max);
      } else {
        this.maxValidateFn = Validators.nullValidator;
      }

      this.onChange(this.value);
    }

    if (Helper.anyChanges(['format'], changes)) {
      this.setInputValue();
    }
  }

  public handleInput(): void {
    const element: HTMLInputElement = this.numericInput.nativeElement;
    const selectionStart = element.selectionStart;
    const selectionEnd = element.selectionEnd;
    const value = element.value;
    if (!this.isValidInput(value)) {
      element.value = this.inputValue;
      this.setSelection(selectionStart - 1, selectionEnd - 1);
    } else {
      const orginalInputValue = this.parseNumber(value);
      let limitInputValue = this.restrictDecimals(orginalInputValue);
      if (this.autoCorrect) {
        limitInputValue = this.limitValue(limitInputValue);
      }

      if (orginalInputValue !== limitInputValue) {
        this.setInputValue(limitInputValue);
        this.setSelection(selectionStart, selectionEnd);
      } else {
        this.inputValue = value;
      }

      this.updateValue(limitInputValue);
    }
  }

  public handleFocus(): void {
    if (!this.focused) {
      this.focused = true;
      this.setInputValue();
      setTimeout(() => this.setSelection(0, this.inputValue.length));
    }
    this.focus.emit();
  }

  public handleBlur(): void {
    if (this.focused) {
      this.focused = false;
      this.onTouched();
      this.setInputValue();
    }
    this.blur.emit();
  }

  public handleKeyDown(event: KeyboardEvent): void {
    if (!this.disabled) {
      switch (event.which) {
        case keyCodes.down:
          this.addStep(-1);
          break;
        case keyCodes.up:
          this.addStep(1);
          break;
        case keyCodes.enter:
          this.enter.emit();
          break;
        case keyCodes.escape:
          this.escape.emit();
          break;
      }
    }
  }

  private verifySettings(): void {
    if (Utils.isNumber(this.min) && Utils.isNumber(this.max) && this.min > this.max) {
      throw new Error('The max value should be bigger than the min value');
    }

    if (Utils.isNumber(this.decimals) && this.decimals < 0) {
      throw new Error('The decimals value should be bigger than 0');
    }
  }
  private isValidInput(input: string) {
    let numericRegex = this.numericRegex;
    if (Utils.isNil(numericRegex)) {
      let hasDecimal = true;
      if (Utils.isNumber(this.decimals) && this.decimals === 0) {
        hasDecimal = false;
      }

      let hasSign = true;
      if (Utils.isNumber(this.min) && this.min >= 0 && this.autoCorrect) {
        hasSign = false;
      }

      numericRegex = Helper.createNumericRegex(hasDecimal, hasSign);
    }

    return numericRegex.test(input);
  }

  private parseNumber(input: string): number {
    return Number(input);
  }

  private addStep(step: number) {
    let value = this.value ? step + this.value : step;
    value = this.limitValue(value);
    value = this.restrictDecimals(value);
    this.setInputValue(value);
    this.updateValue(value);
  }

  private limitValue(value: number): number {
    if (Utils.isNumber(this.max) && value > this.max) {
      return this.max;
    }

    if (Utils.isNumber(this.min) && value < this.min) {
      return this.min;
    }

    return value;
  }

  private isInRange(value: number): boolean {
    if (Utils.isNumber(value)) {
      if (Utils.isNumber(this.min) && value < this.min) {
        return false;
      }

      if (Utils.isNumber(this.max) && value > this.max) {
        return false;
      }
    }

    return true;
  }

  private restrictModelValue(value: number): number {
    let newValue = this.restrictDecimals(value);
    if (this.autoCorrect && this.limitValue(newValue) !== newValue) {
      newValue = null;
    }

    return newValue;
  }

  private restrictDecimals(value: number): number {
    if (Utils.isNumber(this.decimals)) {
      const words = String(value).split('.');
      if (words.length === 2) {
        const decimalPart = words[1];
        if (decimalPart.length > this.decimals) {
          value = parseFloat(words[0] + '.' + decimalPart.substr(0, this.decimals));
        }
      }
    }

    return value;
  }

  private setInputValue(value: number = null) {
    if (Utils.isNil(value)) {
      value = this.value;
    }
    const inputValue = this.formatValue(value);
    this.renderer.setProperty(this.numericInput.nativeElement, 'value', inputValue);
    this.inputValue = inputValue;
  }

  private updateValue(value: number) {
    if (this.value !== value) {
      this.previousValue = this.value;
      this.value = value;
      this.onChange(value);
      this.valueChange.emit(value);
    }
  }

  private formatValue(value: number): string {
    if (!Utils.isNil(value)) {
      return this.formatInputValue(value);
    }

    return '';
  }

  private formatInputValue(value: number): string {
    return String(value);
  }

  private setSelection(start: number, end: number) {
    this.numericInput.nativeElement.setSelectionRange(start, end);
  }
}
